import { Chip } from "@mui/material";
import { GridApiCommon, GridColDef } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { createElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbStatusChange } from "react-icons/tb";

import { TableHeader } from "@/components/TableHeader";
import { passengerStatusOptions, planPassengerStatuses } from "@/constants/passengerStatuses";
import { PASSENGER_STATUS } from "@/constants/status";
import { usePlanActions } from "@/stores/use-plan-table";
import { IPlanPassenger } from "@/types/tasks.types";

import PassengerStatusMileStonesModal from "../PassengerStatusMileStonesModal";
import MemoizedEditAutoComplete from "./EditAutoCompletete";
import MemoizedEditableColumn from "./EditableColumn";

function MemoizedCell({ value, className }: { value: PASSENGER_STATUS; className?: string }) {
    const { t } = useTranslation();

    const status = useMemo(() => planPassengerStatuses.find((item) => item.status === value), [value]);

    return (
        <div className={`w-full h-full flex items-center ${className || ""}`}>
            {status && (
                <Chip
                    sx={{
                        backgroundColor: status.bg,
                        color: status.text,
                        fontWeight: 500,
                        fontSize: "12px !important",
                    }}
                    icon={createElement(status.component(), {
                        fontSize: 14,
                        color: status.text,
                    })}
                    label={t("passengerStatuses." + status.status)}
                />
            )}
        </div>
    );
}

function EditablePassengerStatusColumn({
    value,
    row,
    field,
    api,
    editable,
}: {
    api: GridApiCommon;
    field: string;
    row: IPlanPassenger<"api">;
    value: PASSENGER_STATUS;
    editable: boolean;
}) {
    return (
        <MemoizedEditableColumn
            renderHistoryComp={(val) =>
                val ? <MemoizedCell value={val as PASSENGER_STATUS} className="justify-center" /> : <span>-</span>
            }
            api={api}
            field={field}
            id={row.id}
            editable={editable}
        >
            <PassengerStatusMileStonesModal statusMilestones={row.statusMilestones} rowId={row.id}>
                <div>
                    <MemoizedCell value={value} className="justify-center" />
                </div>
            </PassengerStatusMileStonesModal>
        </MemoizedEditableColumn>
    );
}

function EditCell({
    value,
    row,
    field,
    api,
}: {
    api: GridApiCommon;
    field: string;
    row: IPlanPassenger<"api">;
    value: PASSENGER_STATUS;
}) {
    const { processRowUpdate } = usePlanActions();
    return (
        <MemoizedEditAutoComplete
            processRowUpdate={processRowUpdate}
            placeholder="status"
            icon={TbStatusChange}
            api={api}
            row={row}
            field={field}
            value={value}
            options={passengerStatusOptions}
            optionComp={PassengerOption}
            translation
            labelPrefix="passengerStatuses."
            renderHistoryComp={(val) => (val ? <MemoizedCell value={val as PASSENGER_STATUS} /> : <span>-</span>)}
        />
    );
}

function PassengerOption({ option }: { option: { label: string; value: string | number } }) {
    return <MemoizedCell value={option.value as PASSENGER_STATUS} className="justify-start" />;
}

function PassengerStatusColumn({
    t,
    editable,
}: {
    editable: boolean;
    t: TFunction;
}): GridColDef<IPlanPassenger<"api">> {
    return {
        field: "status",
        headerName: t("status"),
        minWidth: 300,
        valueGetter: (_val, row) => row.status,
        renderHeader: (params) => <TableHeader Component={TbStatusChange} title={params.colDef.headerName!} />,
        renderCell: ({ value, api, field, row }) => (
            <EditablePassengerStatusColumn api={api} field={field} row={row} value={value} editable={editable} />
        ),
        renderEditCell: ({ value, api, row, field }) => <EditCell api={api} field={field} row={row} value={value} />,
        editable,
        type: "singleSelect",
        valueOptions: passengerStatusOptions,
        pastedValueParser: (value: string) => {
            const status = passengerStatusOptions.find((item) => {
                const translatedValue = t("passengerStatuses." + item.label);
                return item.value === value || value === translatedValue;
            });

            return status?.value || undefined;
        },
    };
}
export default PassengerStatusColumn;
